import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import * as Styles from './index.styles';
import { useId, usekey, useClickOutside } from '../../Utils/Hooks';
import { KEYCODE_ESC } from '../../Constants';

const { StyledModal, StyledModalContent, StyledIcon } = Styles;

export const Modal = ({
    visible,
    icon,
    onClose,
    closeOnClickOutside,
    width,
    height,
    children,
    fullbody,
    ...otherProps
}) => {
    const [id] = useId('modal');
    const [fullscreen, setFullscreen] = useState(null);
    const ref = useRef();
    const escPressed = usekey(KEYCODE_ESC);

    useClickOutside(ref, () => {
        if (closeOnClickOutside && visible) {
            onClose();
        }
    });

    const handleResize = () => {
        const diff = window.innerWidth - width;
        if (diff <= 0) {
            setFullscreen(true);
        } else {
            setFullscreen(false);
        }
    };

    useEffect(() => {
        if (visible) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
    }, [visible]);

    useEffect(() => {
        if (escPressed && visible) {
            onClose();
        }
    }, [escPressed]);

    useEffect(() => {
        handleResize();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            document.body.style.overflow = '';
        };
    }, []);

    return ReactDOM.createPortal(
        <StyledModal id={id} visible={visible} {...otherProps}>
            <StyledModalContent
                fullbody={fullbody}
                fullscreen={fullscreen}
                width={width}
                height={height}
                ref={ref}
            >
                {icon && <StyledIcon icon={['fal', 'xmark']} onClick={onClose} />}
                {children}
            </StyledModalContent>
        </StyledModal>,
        document.body,
    );
};

Modal.propTypes = {
    visible: PropTypes.bool,
    closeOnClickOutside: PropTypes.bool,
    icon: PropTypes.bool,
    width: PropTypes.number,
    height: PropTypes.number,
    onClose: PropTypes.func,
    children: PropTypes.node.isRequired,
    fullbody: PropTypes.bool,
};

/* istanbul ignore next */
Modal.defaultProps = {
    visible: false,
    closeOnClickOutside: true,
    icon: true,
    width: 600,
    height: undefined,
    onClose: () => null,
    fullbody: false,
};

Modal.Styles = Styles;

export * from './ModalHeader';
export * from './ModalBody';
export * from './ModalFooter';
